import React, {useState} from 'react';
import {AdCampaign, AdGroup, TrackingSourceType} from '../../../service/MetaService';

export interface AdminKedAdsResultsProps {
    campaigns: AdCampaign[];
}

export default function AdminKedAdsResults(props: AdminKedAdsResultsProps) {
    const {campaigns} = props;
    const [selectedCampaign, setSelectedCampaign] = useState<AdCampaign | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [expandedCampaignIds, setExpandedCampaignIds] = useState<Set<string>>(new Set());
    const [adGroupsByCompany, setAdGroupsByCompany] = useState<Record<string, AdGroup[]>>({});
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' | null }>({
        key: '',
        direction: null
    });
    const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(true);
    const [originalCampaigns] = useState<AdCampaign[]>(campaigns);
    const [trackingSource, setTrackingSource] = useState<TrackingSourceType>('facebook');

    // Define labels for tracking source options
    const trackingSourceOptions = [
        {value: 'facebook', label: 'Facebook Attribution'},
        {value: 'firstSource', label: 'Scientific Tracking (First Source)'},
        {value: 'lastSource', label: 'Scientific Tracking (Last Source)'}
    ];

    // Function to get tracking data based on selected source type
    const getTrackingData = (campaign: AdCampaign, field: string) => {
        switch (trackingSource) {
            case 'firstSource':
                switch (field) {
                    case 'leads':
                        return campaign.realLeadsFirstSource;
                    case 'watches':
                        return campaign.realWatchesFirstSource;
                    case 'initTransactions':
                        return campaign.realInitTransactionsFirstSource;
                    case 'purchases':
                        return campaign.realPurchasesFirstSource;
                    case 'purchaseValue':
                        return campaign.realPurchasesFirstSourceValue;
                    case 'roas':
                        return campaign.realRoasFirstSource;
                    case 'costPerLead':
                        return campaign.realCostPerLeadFirstSource;
                    default:
                        return 0;
                }
            case 'lastSource':
                switch (field) {
                    case 'leads':
                        return campaign.realLeadsLastSource;
                    case 'watches':
                        return campaign.realWatchesLastSource;
                    case 'initTransactions':
                        return campaign.realInitTransactionsLastSource;
                    case 'purchases':
                        return campaign.realPurchasesLastSource;
                    case 'purchaseValue':
                        return campaign.realPurchasesLastSourceValue;
                    case 'roas':
                        return campaign.realRoasLastSource;
                    case 'costPerLead':
                        return campaign.realCostPerLeadLastSource;
                    default:
                        return 0;
                }
            default:
                switch (field) {
                    case 'leads':
                        return campaign.realLeads;
                    case 'watches':
                        return campaign.realWatches;
                    case 'initTransactions':
                        return campaign.realInitTransactions;
                    case 'purchases':
                        return campaign.realPurchases;
                    case 'purchaseValue':
                        return campaign.realPurchaseValue;
                    case 'roas':
                        return campaign.realRoas;
                    case 'costPerLead':
                        return campaign.realCostPerLead;
                    default:
                        return 0;
                }
        }
    };

    // Similar function for ad groups
    const getAdGroupTrackingData = (adGroup: AdGroup, field: string) => {
        switch (trackingSource) {
            case 'firstSource':
                switch (field) {
                    case 'leads':
                        return adGroup.realLeadsFirstSource;
                    case 'watches':
                        return adGroup.realWatchesFirstSource;
                    case 'initTransactions':
                        return adGroup.realInitTransactionsFirstSource;
                    case 'purchases':
                        return adGroup.realPurchasesFirstSource;
                    case 'purchaseValue':
                        return adGroup.realPurchasesFirstSourceValue;
                    case 'roas':
                        return adGroup.realRoasFirstSource;
                    case 'costPerLead':
                        return adGroup.realCostPerLeadFirstSource;
                    default:
                        return 0;
                }
            case 'lastSource':
                switch (field) {
                    case 'leads':
                        return adGroup.realLeadsLastSource;
                    case 'watches':
                        return adGroup.realWatchesLastSource;
                    case 'initTransactions':
                        return adGroup.realInitTransactionsLastSource;
                    case 'purchases':
                        return adGroup.realPurchasesLastSource;
                    case 'purchaseValue':
                        return adGroup.realPurchasesLastSourceValue;
                    case 'roas':
                        return adGroup.realRoasLastSource;
                    case 'costPerLead':
                        return adGroup.realCostPerLeadLastSource;
                    default:
                        return 0;
                }
            default:
                switch (field) {
                    case 'leads':
                        return adGroup.realLeads;
                    case 'watches':
                        return adGroup.realWatches;
                    case 'initTransactions':
                        return adGroup.realInitTransactions;
                    case 'purchases':
                        return adGroup.realPurchases;
                    case 'purchaseValue':
                        return adGroup.realPurchaseValue;
                    case 'roas':
                        return adGroup.realRoas;
                    case 'costPerLead':
                        return adGroup.realCostPerLead;
                    default:
                        return 0;
                }
        }
    };

    const handleShowDetails = (campaign: AdCampaign) => {
        setSelectedCampaign(campaign);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleToggleAdGroups = async (campaign: AdCampaign, event: React.MouseEvent) => {
        // Don't expand if clicking on the "Więcej informacji" button
        if ((event.target as HTMLElement).closest('button')) {
            return;
        }

        const newExpandedIds = new Set(expandedCampaignIds);

        if (expandedCampaignIds.has(campaign.id)) {
            newExpandedIds.delete(campaign.id);
        } else {
            newExpandedIds.add(campaign.id);

            // Access adSets that are already included in the campaign data
            if (!adGroupsByCompany[campaign.id] && campaign.adSets) {
                setAdGroupsByCompany((prev: any) => ({
                    ...prev,
                    [campaign.id]: campaign.adSets
                }));
            }
        }

        setExpandedCampaignIds(newExpandedIds);
    };

    // Helper function to determine ROAS styling class
    const getRoasClass = (roas: number): string => {
        if (roas >= 3) return 'bg-success text-white';
        if (roas >= 2) return '';
        if (roas >= 1.2) return 'bg-warning';
        return 'bg-danger text-white';
    };

    // Helper function to check if campaign is inactive
    const isCampaignInactive = (campaign: AdCampaign): boolean => {
        return campaign.status?.toLowerCase() !== 'active';
    };

    // Function to get text style for inactive items
    const getInactiveStyle = (): React.CSSProperties => {
        return {
            color: '#dc3545',
            textDecoration: 'line-through',
            fontWeight: 'normal'
        };
    };

    // Sorting function
    const requestSort = (key: string) => {
        let direction: 'ascending' | 'descending' | null = 'ascending';

        if (sortConfig.key === key) {
            if (sortConfig.direction === 'ascending') {
                direction = 'descending';
            } else if (sortConfig.direction === 'descending') {
                direction = null;
            }
        }

        setSortConfig({key, direction});
    };

    // Reset sorting to original order
    const resetSort = () => {
        setSortConfig({key: '', direction: null});
    };

    // Get the sorted campaigns
    const getSortedCampaigns = () => {
        let filteredCampaigns = [...campaigns];

        // Filter out inactive campaigns if toggle is off
        if (!showInactiveCampaigns) {
            filteredCampaigns = filteredCampaigns.filter(campaign => !isCampaignInactive(campaign));
        }

        // If no sorting is applied, return the filtered campaigns
        if (!sortConfig.key || sortConfig.direction === null) {
            return filteredCampaigns;
        }

        // Sort the campaigns
        return [...filteredCampaigns].sort((a, b) => {
            // Handle numeric values for sorting
            const getValue = (item: any, key: string) => {
                const value = key.includes('.')
                    ? key.split('.').reduce((obj, k) => obj && obj[k], item)
                    : item[key];

                if (value === undefined || value === null) return 0;

                // Check if it's a numeric string and convert to number for sorting
                const numValue = Number(value);
                return isNaN(numValue) ? value : numValue;
            };

            const aValue = getValue(a, sortConfig.key);
            const bValue = getValue(b, sortConfig.key);

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    };

    // Helper function to render sort indicator
    const getSortIndicator = (key: string) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    };

    // Get sorted campaigns
    const sortedCampaigns = getSortedCampaigns();

    if (!campaigns || campaigns.length === 0) {
        return (
            <div className="alert alert-info text-center my-8">
                Nie znaleziono żadnych kampanii reklamowych.
            </div>
        );
    }

    // Calculate totals for summary row based on tracking source
    const totals = {
        spent: campaigns.reduce((sum, campaign) => sum + campaign.spent, 0),
        impressions: campaigns.reduce((sum, campaign) => sum + campaign.impressions, 0),
        reach: campaigns.reduce((sum, campaign) => sum + campaign.reach, 0),
        linkClicks: campaigns.reduce((sum, campaign) => sum + campaign.linkClicks, 0),
        uniqueClicks: campaigns.reduce((sum, campaign) => sum + campaign.uniqueClicks, 0),
        leads: campaigns.reduce((sum, campaign) => sum + campaign.leads, 0),
        purchases: campaigns.reduce((sum, campaign) => sum + campaign.purchases, 0),
        purchaseValue: campaigns.reduce((sum, campaign) => sum + campaign.purchaseValue, 0),

        // Dynamic totals based on tracking source
        realLeads: campaigns.reduce((sum, campaign) => sum + getTrackingData(campaign, 'leads'), 0),
        realWatches: campaigns.reduce((sum, campaign) => sum + getTrackingData(campaign, 'watches'), 0),
        realInitTransactions: campaigns.reduce((sum, campaign) => sum + getTrackingData(campaign, 'initTransactions'), 0),
        realPurchases: campaigns.reduce((sum, campaign) => sum + getTrackingData(campaign, 'purchases'), 0),
        realPurchaseValue: campaigns.reduce((sum, campaign) => sum + getTrackingData(campaign, 'purchaseValue'), 0),
    };

    // Calculate derived metrics for totals
    const totalCpm = totals.impressions > 0 ? ((totals.spent / totals.impressions) * 1000) : 0;
    const totalCtrLink = totals.impressions > 0 ? ((totals.linkClicks / totals.impressions) * 100) : 0;
    const totalCostPerLinkClick = totals.linkClicks > 0 ? (totals.spent / totals.linkClicks) : 0;
    const totalCostPerLead = totals.leads > 0 ? (totals.spent / totals.leads) : 0;
    const totalCostPerRealLead = totals.realLeads > 0 ? (totals.spent / totals.realLeads) : 0;
    const totalCostPerPurchase = totals.purchases > 0 ? (totals.spent / totals.purchases) : 0;
    const totalRoas = totals.spent > 0 ? (totals.purchaseValue / totals.spent) : 0;
    const totalRealRoas = totals.spent > 0 ? (totals.realPurchaseValue / totals.spent) : 0;
    const totalLeadConversionRate = totals.leads > 0 ? ((totals.purchases / totals.leads) * 100) : 0;
    const totalRealLeadConversionRate = totals.realLeads > 0 ? ((totals.realPurchases / totals.realLeads) * 100) : 0;
    const totalLeadPercentage = totals.linkClicks > 0 ? ((totals.leads / totals.linkClicks) * 100) : 0;
    const totalRealLeadPercentage = totals.linkClicks > 0 ? ((totals.realLeads / totals.linkClicks) * 100) : 0;

    return (
        <div className="mb-20">
            {/* Tracking Source Selector */}
            <div className="card bg-white mb-4 p-3">
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                    <div>
                        <label htmlFor="tracking-source" className="form-label mb-1">Źródło śledzenia:</label>
                        <select
                            id="tracking-source"
                            className="form-select"
                            value={trackingSource}
                            onChange={(e) => setTrackingSource(e.target.value as TrackingSourceType)}
                        >
                            {trackingSourceOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-12">
                    <div className="card bg-white shadow-sm">
                        <div className="card-body p-4">
                            <h3 className="fs-7 fw-bold mb-3 text-dark">Podsumowanie</h3>
                            <div className="row">
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Wydatki całkowite</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totals.spent, 'currency')}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Wyświetlenia</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totals.impressions)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Kliknięcia w link</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totals.linkClicks)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">CTR (link)</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totalCtrLink, 'percentage')}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">CPM</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totalCpm, 'currency', 2)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Koszt/kliknięcie w link</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totalCostPerLinkClick, 'currency', 2)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Leady</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{`${formatNumber(totals.leads)} (${formatNumber(totals.realLeads)})`}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">LP%</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{`${formatNumber(totalLeadPercentage, 'percentage')} (${formatNumber(totalRealLeadPercentage, 'percentage')})`}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Koszt/lead</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">
                                                {totals.leads > 0 ? `${formatNumber(totalCostPerLead, 'currency', 2)} (${formatNumber(totalCostPerRealLead, 'currency', 2)})` : '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Watches</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totals.realWatches)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">InitT.</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{formatNumber(totals.realInitTransactions)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Zakupy</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{`${formatNumber(totals.purchases)} (${formatNumber(totals.realPurchases)})`}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">Wartość zakupów</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{`${formatNumber(totals.purchaseValue, 'currency')} (${formatNumber(totals.realPurchaseValue, 'currency')})`}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">ROAS</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">
                                                <span className={`px-2 py-1 rounded ${getRoasClass(totalRoas)}`}>
                                                    {formatNumber(totalRoas, 'number', 2)}x
                                                </span>
                                                {' '}
                                                (
                                                <span className={`px-2 py-1 rounded ${getRoasClass(totalRealRoas)}`}>
                                                    {formatNumber(totalRealRoas, 'number', 2)}x
                                                </span>
                                                )
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body py-3">
                                            <h4 className="fs-10 text-muted mb-1">LC%</h4>
                                            <p className="fs-8 fw-bold mb-0 text-dark">{`${formatNumber(totalLeadConversionRate, 'percentage')} (${formatNumber(totalRealLeadConversionRate, 'percentage')})`}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm bg-white">
                <div className="card-body p-6">
                    <h2 className="fs-7 fw-bold mb-4 text-dark">Szczegóły kampanii</h2>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetSort}
                        >
                            <i className="bi bi-arrow-repeat me-1"></i> Sortuj domyślnie
                        </button>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showInactiveCampaigns"
                                checked={showInactiveCampaigns}
                                onChange={() => setShowInactiveCampaigns(!showInactiveCampaigns)}
                            />
                            <label className="form-check-label" htmlFor="showInactiveCampaigns">
                                Pokaż nieaktywne kampanie
                            </label>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover table-striped">
                            <thead className="table-dark">
                            <tr>
                                <th onClick={() => requestSort('name')} style={{cursor: 'pointer'}}>
                                    Nazwa {getSortIndicator('name')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('spent')}
                                    style={{cursor: 'pointer'}}>
                                    Wydatki {getSortIndicator('spent')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('impressions')}
                                    style={{cursor: 'pointer'}}>
                                    Wyświetlenia {getSortIndicator('impressions')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('cpm')} style={{cursor: 'pointer'}}>
                                    CPM {getSortIndicator('cpm')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('linkClicks')}
                                    style={{cursor: 'pointer'}}>
                                    Kliknięcia link {getSortIndicator('linkClicks')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('ctrLink')}
                                    style={{cursor: 'pointer'}}>
                                    CTR (link) {getSortIndicator('ctrLink')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('costPerLinkClick')}
                                    style={{cursor: 'pointer'}}>
                                    Koszt/klik link {getSortIndicator('costPerLinkClick')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('leads')}
                                    style={{cursor: 'pointer'}}>
                                    Leady {getSortIndicator('leads')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('leads')}
                                    style={{cursor: 'pointer'}}>
                                    LP% {getSortIndicator('leads')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('costPerLead')}
                                    style={{cursor: 'pointer'}}>
                                    Koszt/lead {getSortIndicator('costPerLead')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('realWatches')}
                                    style={{cursor: 'pointer'}}>
                                    Watches {getSortIndicator('realWatches')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('realInitTransactions')}
                                    style={{cursor: 'pointer'}}>
                                    InitT. {getSortIndicator('realInitTransactions')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('purchases')}
                                    style={{cursor: 'pointer'}}>
                                    Zakupy {getSortIndicator('purchases')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('leads')}
                                    style={{cursor: 'pointer'}}>
                                    LC% {getSortIndicator('leads')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('purchaseValue')}
                                    style={{cursor: 'pointer'}}>
                                    Wartość zakupów {getSortIndicator('purchaseValue')}
                                </th>
                                <th className="text-end" onClick={() => requestSort('roas')}
                                    style={{cursor: 'pointer'}}>
                                    ROAS {getSortIndicator('roas')}
                                </th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedCampaigns.map((campaign) => (
                                <React.Fragment key={campaign.id}>
                                    <tr
                                        onClick={(e) => handleToggleAdGroups(campaign, e)}
                                        style={{cursor: 'pointer'}}
                                        className={expandedCampaignIds.has(campaign.id) ? 'table-active' : ''}
                                    >
                                        <td>
                                            <div className="d-flex align-items-start">
                                                <span className="me-2">
                                                    {expandedCampaignIds.has(campaign.id) ?
                                                        <i className="bi bi-dash-square"></i> :
                                                        <i className="bi bi-plus-square"></i>}
                                                </span>
                                                <div>
                                                    <div
                                                        className={`text-truncate ${isCampaignInactive(campaign) ? 'text-decoration-line-through text-danger' : ''}`}
                                                        style={{maxWidth: "300px"}}
                                                        title={campaign.name}>
                                                        {campaign.name}
                                                    </div>
                                                    <div className="text-muted fs-10">{campaign.id}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-end">{formatNumber(campaign.spent, 'currency')}</td>
                                        <td className="text-end">{formatNumber(campaign.impressions)}</td>
                                        <td className="text-end">{formatNumber(campaign.cpm, 'currency', 2)}</td>
                                        <td className="text-end">{formatNumber(campaign.linkClicks)}</td>
                                        <td className="text-end">{formatNumber(campaign.ctrLink, 'percentage')}</td>
                                        <td className="text-end">{formatNumber(campaign.costPerLinkClick, 'currency', 2)}</td>
                                        <td className="text-end">{`${formatNumber(campaign.leads)} (${formatNumber(getTrackingData(campaign, 'leads'))})`}</td>
                                        <td className="text-end">{`${
                                            campaign.linkClicks > 0 ?
                                                formatNumber((campaign.leads / campaign.linkClicks) * 100, 'percentage') :
                                                '0%'
                                        } (${
                                            campaign.linkClicks > 0 ?
                                                formatNumber((getTrackingData(campaign, 'leads') / campaign.linkClicks) * 100, 'percentage') :
                                                '0%'
                                        })`}</td>
                                        <td className="text-end">{`${formatNumber(campaign.costPerLead, 'currency', 2)} (${formatNumber(getTrackingData(campaign, 'costPerLead'), 'currency', 2)})`}</td>
                                        <td className="text-end">{formatNumber(getTrackingData(campaign, 'watches'))}</td>
                                        <td className="text-end">{formatNumber(getTrackingData(campaign, 'initTransactions'))}</td>
                                        <td className="text-end">{`${formatNumber(campaign.purchases)} (${formatNumber(getTrackingData(campaign, 'purchases'))})`}</td>
                                        <td className="text-end">{`${
                                            campaign.leads > 0 ?
                                                formatNumber((campaign.purchases / campaign.leads) * 100, 'percentage') :
                                                '0%'
                                        } (${
                                            getTrackingData(campaign, 'leads') > 0 ?
                                                formatNumber((getTrackingData(campaign, 'purchases') / getTrackingData(campaign, 'leads')) * 100, 'percentage') :
                                                '0%'
                                        })`}</td>
                                        <td className="text-end">{`${formatNumber(campaign.purchaseValue, 'currency')} (${formatNumber(getTrackingData(campaign, 'purchaseValue'), 'currency')})`}</td>
                                        <td className="text-end">
                                            <span
                                                className={`px-2 py-1 rounded ${getRoasClass(campaign.roas)}`}>
                                                {formatNumber(campaign.roas, 'number', 2)}x
                                            </span>
                                            {' '}
                                            (
                                            <span
                                                className={`px-2 py-1 rounded ${getRoasClass(getTrackingData(campaign, 'roas'))}`}>
                                                {formatNumber(getTrackingData(campaign, 'roas'), 'number', 2)}x
                                            </span>
                                            )
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleShowDetails(campaign)}
                                            >
                                                Info
                                            </button>
                                        </td>
                                    </tr>

                                    {/* Ad groups rows - visible when campaign is expanded */}
                                    {expandedCampaignIds.has(campaign.id) && adGroupsByCompany[campaign.id] && (
                                        adGroupsByCompany[campaign.id].map((adGroup) => {
                                            const isInactive = isCampaignInactive(campaign) || adGroup.status?.toLowerCase() !== 'active';
                                            const adSetStyle = {
                                                backgroundColor: '#f8f9ff',
                                                fontSize: '0.9rem',
                                                borderLeft: '4px solid #ced4da',
                                            };

                                            // Apply text styling to all td elements for consistency
                                            const textStyle = isInactive ? getInactiveStyle() : {};

                                            return (
                                                <tr key={`${campaign.id}-${adGroup.id}`} style={adSetStyle}>
                                                    <td>
                                                        <div style={{paddingLeft: '1rem', ...textStyle}}>
                                                            <div
                                                                className={`ps-4 text-truncate ${isInactive ? 'text-decoration-line-through' : ''}`}
                                                                style={{maxWidth: "300px", ...textStyle}}
                                                                title={adGroup.name}>
                                                                <i className="bi bi-diagram-3 me-2 text-secondary"></i>
                                                                {adGroup.name}
                                                            </div>
                                                            <div className="text-muted ps-4"
                                                                 style={{fontSize: '10px'}}>{adGroup.id}</div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.spent, 'currency')}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.impressions)}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.cpm, 'currency', 2)}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.linkClicks)}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.ctrLink, 'percentage')}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(adGroup.costPerLinkClick, 'currency', 2)}</td>
                                                    <td className="text-end">{`${formatNumber(adGroup.leads)} (${formatNumber(getAdGroupTrackingData(adGroup, 'leads'))})`}</td>
                                                    <td className="text-end">{`${
                                                        adGroup.linkClicks > 0 ?
                                                            formatNumber((adGroup.leads / adGroup.linkClicks) * 100, 'percentage') :
                                                            '0%'
                                                    } (${
                                                        adGroup.linkClicks > 0 ?
                                                            formatNumber((getAdGroupTrackingData(adGroup, 'leads') / adGroup.linkClicks) * 100, 'percentage') :
                                                            '0%'
                                                    })`}</td>
                                                    <td className="text-end">{`${formatNumber(adGroup.costPerLead, 'currency', 2)} (${formatNumber(getAdGroupTrackingData(adGroup, 'costPerLead'), 'currency', 2)})`}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(getAdGroupTrackingData(adGroup, 'watches'))}</td>
                                                    <td className="text-end"
                                                        style={textStyle}>{formatNumber(getAdGroupTrackingData(adGroup, 'initTransactions'))}</td>
                                                    <td className="text-end">{`${formatNumber(adGroup.purchases)} (${formatNumber(getAdGroupTrackingData(adGroup, 'purchases'))})`}</td>
                                                    <td className="text-end">{`${
                                                        adGroup.leads > 0 ?
                                                            formatNumber((adGroup.purchases / adGroup.leads) * 100, 'percentage') :
                                                            '0%'
                                                    } (${
                                                        getAdGroupTrackingData(adGroup, 'leads') > 0 ?
                                                            formatNumber((getAdGroupTrackingData(adGroup, 'purchases') / getAdGroupTrackingData(adGroup, 'leads')) * 100, 'percentage') :
                                                            '0%'
                                                    })`}</td>
                                                    <td className="text-end">{`${formatNumber(adGroup.purchaseValue, 'currency')} (${formatNumber(getAdGroupTrackingData(adGroup, 'purchaseValue'), 'currency')})`}</td>
                                                    <td className="text-end">
                                                        <span
                                                            className={`px-2 py-1 rounded ${getRoasClass(adGroup.roas)}`}>
                                                            {formatNumber(adGroup.roas, 'number', 2)}x
                                                        </span>
                                                        {' '}
                                                        (
                                                        <span
                                                            className={`px-2 py-1 rounded ${getRoasClass(getAdGroupTrackingData(adGroup, 'roas'))}`}>
                                                            {formatNumber(getAdGroupTrackingData(adGroup, 'roas'), 'number', 2)}x
                                                        </span>
                                                        )
                                                    </td>
                                                    <td style={textStyle}></td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                            <tfoot className="table-dark">
                            <tr>
                                <td className="fw-bold">SUMA</td>
                                <td>{formatNumber(totals.spent, 'currency')}</td>
                                <td>{formatNumber(totals.impressions)}</td>
                                <td>{formatNumber(totalCpm, 'currency', 2)}</td>
                                <td>{formatNumber(totals.linkClicks)}</td>
                                <td>{formatNumber(totalCtrLink, 'percentage')}</td>
                                <td>{formatNumber(totalCostPerLinkClick, 'currency', 2)}</td>
                                <td>{`${formatNumber(totals.leads)} (${formatNumber(totals.realLeads)})`}</td>
                                <td>{`${formatNumber(totalLeadPercentage, 'percentage')} (${formatNumber(totalRealLeadPercentage, 'percentage')})`}</td>
                                <td>{totals.leads > 0 ? `${formatNumber(totalCostPerLead, 'currency', 2)} (${formatNumber(totalCostPerRealLead, 'currency', 2)})` : '-'}</td>
                                <td>{formatNumber(totals.realWatches)}</td>
                                <td>{formatNumber(totals.realInitTransactions)}</td>
                                <td>{`${formatNumber(totals.purchases)} (${formatNumber(totals.realPurchases)})`}</td>
                                <td>{`${formatNumber(totalLeadConversionRate, 'percentage')} (${formatNumber(totalRealLeadConversionRate, 'percentage')})`}</td>
                                <td>{`${formatNumber(totals.purchaseValue, 'currency')} (${formatNumber(totals.realPurchaseValue, 'currency')})`}</td>
                                <td>
                                    <span className={`px-2 py-1 rounded ${getRoasClass(totalRoas)}`}>
                                        {formatNumber(totalRoas, 'number', 2)}x
                                    </span>
                                    {' '}
                                    (
                                    <span className={`px-2 py-1 rounded ${getRoasClass(totalRealRoas)}`}>
                                        {formatNumber(totalRealRoas, 'number', 2)}x
                                    </span>
                                    )
                                </td>
                                <td></td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>

            {/* Campaign Details Modal - Using plain Bootstrap */}
            {showModal && (
                <div className="modal fade show" style={{display: 'block'}} tabIndex={-1} role="dialog">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title fs-7 text-dark">
                                    Szczegóły kampanii: {selectedCampaign?.name}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}
                                        aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                {selectedCampaign && (
                                    <div className="row">
                                        <div className="col-12 mb-3">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h5 className="fs-8 fw-bold mb-0 text-dark">Informacje
                                                        podstawowe</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-6 mb-2">
                                                            <p className="mb-0 text-dark"><strong>ID
                                                                kampanii:</strong> {selectedCampaign.id}</p>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Status:</strong> {selectedCampaign.status}</p>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Cel:</strong> {selectedCampaign.objective}</p>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Budżet
                                                                    dzienny:</strong> {selectedCampaign.dailyBudget ? formatNumber(selectedCampaign.dailyBudget, 'currency') : 'Nie ustawiono'}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Budżet
                                                                    całkowity:</strong> {selectedCampaign.lifetimeBudget ? formatNumber(selectedCampaign.lifetimeBudget, 'currency') : 'Nie ustawiono'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h5 className="fs-8 fw-bold mb-0 text-dark">Statystyki kampanii</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Wydatki:</strong> {formatNumber(selectedCampaign.spent, 'currency')}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Wyświetlenia:</strong> {formatNumber(selectedCampaign.impressions)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Zasięg:</strong> {formatNumber(selectedCampaign.reach)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>CPM:</strong> {formatNumber(selectedCampaign.cpm, 'currency', 2)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Wszystkie
                                                                kliknięcia:</strong> {formatNumber(selectedCampaign.clicks)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Unikalne
                                                                kliknięcia:</strong> {formatNumber(selectedCampaign.uniqueClicks)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Kliknięcia w
                                                                link:</strong> {formatNumber(selectedCampaign.linkClicks)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Kliknięcia
                                                                inline:</strong> {formatNumber(selectedCampaign.inlineLinkClicks)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>CTR
                                                                (wszystkie):</strong> {formatNumber(selectedCampaign.ctr, 'percentage')}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>CTR
                                                                (link):</strong> {formatNumber(selectedCampaign.ctrLink, 'percentage')}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Koszt/klik
                                                                (wszystkie):</strong> {formatNumber(selectedCampaign.costPerUniqueClick, 'currency', 2)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark"><strong>Koszt/klik
                                                                (link):</strong> {formatNumber(selectedCampaign.costPerLinkClick, 'currency', 2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-3">
                                            <div className="card">
                                                <div className="card-header bg-light">
                                                    <h5 className="fs-8 fw-bold mb-0 text-dark">Dane konwersji</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row">
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Wszystkie
                                                                    konwersje:</strong> {formatNumber(selectedCampaign.conversions)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Leady:</strong> {formatNumber(selectedCampaign.leads)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Otwarcia
                                                                    formularza:</strong> {formatNumber(selectedCampaign.leadFormOpens)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Koszt/lead:</strong> {isNaN(selectedCampaign.costPerLead) ? formatNumber(selectedCampaign.costPerLead, 'currency', 2) : '-'}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Zakupy:</strong> {formatNumber(selectedCampaign.purchases)}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Wartość
                                                                    zakupów:</strong> {formatNumber(selectedCampaign.purchaseValue, 'currency')}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>Koszt/zakup:</strong> {isNaN(selectedCampaign.costPerPurchase) ? formatNumber(selectedCampaign.costPerPurchase, 'currency', 2) : '-'}
                                                            </p>
                                                        </div>
                                                        <div className="col-md-4 mb-2">
                                                            <p className="mb-0 text-dark">
                                                                <strong>ROAS:</strong> {formatNumber(selectedCampaign.roas, 'number', 2)}x
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {selectedCampaign.videoP25Watched && selectedCampaign.videoP25Watched > 0 && (
                                            <div className="col-12 mb-3">
                                                <div className="card">
                                                    <div className="card-header bg-light">
                                                        <h5 className="fs-8 fw-bold mb-0 text-dark">Statystyki
                                                            wideo</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="row">
                                                            <div className="col-md-3 mb-2">
                                                                <p className="mb-0 text-dark">
                                                                    <strong>Obejrzano
                                                                        25%:</strong> {formatNumber(selectedCampaign.videoP25Watched)}
                                                                </p>
                                                            </div>
                                                            <div className="col-md-3 mb-2">
                                                                <p className="mb-0 text-dark">
                                                                    <strong>Obejrzano
                                                                        50%:</strong> {formatNumber(selectedCampaign.videoP50Watched)}
                                                                </p>
                                                            </div>
                                                            <div className="col-md-3 mb-2">
                                                                <p className="mb-0 text-dark">
                                                                    <strong>Obejrzano
                                                                        75%:</strong> {formatNumber(selectedCampaign.videoP75Watched)}
                                                                </p>
                                                            </div>
                                                            <div className="col-md-3 mb-2">
                                                                <p className="mb-0 text-dark">
                                                                    <strong>Obejrzano
                                                                        100%:</strong> {formatNumber(selectedCampaign.videoP100Watched)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    Zamknij
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal backdrop */}
            {showModal && <div className="modal-backdrop fade show"></div>}
        </div>
    );
}

// Centralized formatting function to ensure consistency
function formatNumber(value: string | number | undefined, format: 'number' | 'currency' | 'percentage' = 'number', decimals: number = 0): string {
    if (value === undefined || value === null || value === '') {
        return '-';
    }

    const numericValue = typeof value === 'string' ? Number(value.replace(/[^\d.-]/g, '')) : value;

    if (isNaN(numericValue)) {
        return '-';
    }

    switch (format) {
        case 'currency':
            return new Intl.NumberFormat('pl-PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }).format(numericValue);

        case 'percentage':
            // Handle percentage strings already formatted with %
            if (typeof value === 'string' && value.includes('%')) {
                return value;
            }
            return `${numericValue.toFixed(2)}%`;

        case 'number':
        default:
            return new Intl.NumberFormat('pl-PL', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }).format(numericValue);
    }
}

// Date formatting function
function formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL');
}
